/**
 * Data-driven class AI kit interpreter.
 * Kits live in tables/raid/ai-behavior-kits.json; geometry from raid-motion/engage.
 * When/score: raid-kit-score.ts.
 */

import { raidCatalog, type RaidClassId } from "../../data/raid-catalog";
import type { RaidBoss, RaidUnit } from "./raid-types";
import {
  type AiIntent,
  type EngageTarget,
  stickyEngageTarget,
  hostileAttackReach,
  hostileHardMinSep,
  addAttackReach,
  resolveAddRadius,
  dist,
  faceTarget,
  faceBoss,
  facePoint,
  classKnobs,
  pickInWeaponReachAdd,
} from "./raid-engage";
import {
  spaceOutBossGoal,
  ringPointFromUnit,
  resolveGoalInBounds,
  applySlot,
  applySlotNearHostile,
  clampGoalToAddReach,
  allyInBrass,
  healSplitAlly,
  pickInRangeHealNeed,
  frontTank,
  pickHealerSupportGoal,
  retreatToHealerGoal,
  formationReference,
  gestureFormationWeight,
  applyMeasurePlantGoal,
  inRangeStandDeadzone,
  allyAnchor,
  raidPlayBounds,
  finalizeAllyMoveGoal,
  resolvePackEscapeGoal,
} from "./raid-motion";
import {
  blendKitRingWithAdjutantSeed,
  blendSpaceGoalWithAdjutant,
  effectiveAdjutantFollow,
  healerBacklineBehindTank,
  livingHealersRanked,
  planAdjutant,
  shouldConsumeAdjutantSeed,
  shouldSoftLateralAdjutantSeed,
  slotFor,
  threatFacingForBackline,
  worldLerpTowardAdjutantSeed,
} from "./raid-adjutant";
import {
  dispositionKnob,
  dispositionOf,
  insightAxisOf,
  instinctOf,
} from "./raid-disposition";
import {
  biasAdjutantSeedForRescue,
  coverSubjectOutOfStaff,
  instinctAllowsAoEDodge,
  judgmentOf,
  livingHealers,
  pickCoverSubject,
  resolveSoftSafeFocusId,
  resolveSoftSafeHealNeed,
  softAaHealBelowPct,
} from "./judgment";
import {
  clearOfEnemyAttack,
  clearOfPackChewAttack,
  collectRaidDangerZones,
  effectiveAaClearPadPx,
  inEnemyMeleeDisk,
  kiteStandOnLocalHostile,
  localHostileSnapshot,
  nearEnemyHitOrMelee,
  raidPackAllyPull,
  raidPackFleeDist,
  raidPackRadius,
  raidSoftPlantMax,
  spaceFromLocalHostileGoal,
  unitInDangerZones,
} from "./raid-threat";
import {
  type KitDecideCtx,
  type KitRuntime,
  type WhenClause,
  kitById as kitByIdLoose,
  kitCombatNum,
  kitCombatTable,
  kitKnobOn,
  whenMatches,
} from "./raid-kit-score";

export type { KitDecideCtx, KitRuntime } from "./raid-kit-score";
export { defaultKitPorts, scoreFromKit } from "./raid-kit-score";

type CombatTable = (typeof raidCatalog.balance)["combat"];

const combatTable = kitCombatTable;
const combatNum = kitCombatNum;
const knobOn = kitKnobOn;

/** Bond follow × screen urgency (+ AoE spread emergency for bond≈0). */
function followUnderScreen(
  followMul: number,
  screen: number,
  spread = 0,
): number {
  return effectiveAdjutantFollow(followMul, screen, undefined, spread);
}

/** Re-export plant blend for raid-rules swing-stop (SSOT in raid-motion). */
export {
  plantBlendFromMeasure,
  SWING_STOP_PLANT_BLEND,
} from "./raid-motion";

function healBelowPct(
  u: RaidUnit,
  role: RaidClassId,
  rt: KitRuntime,
): number {
  const knobs = classKnobs(role) as Record<string, unknown>;
  const catalogPct =
    typeof knobs.healLowestAllyBelowPct === "number"
      ? knobs.healLowestAllyBelowPct
      : 0.72;
  return softAaHealBelowPct(catalogPct) * rt.bondHealMul(u);
}

/** Approach anchor: in-range need first, else split triage for walk-to. */
function pickHealNeed(
  u: RaidUnit,
  allies: readonly RaidUnit[],
  role: RaidClassId,
  rt: KitRuntime,
  enemies: readonly RaidUnit[] = [],
): RaidUnit | null {
  const pct = healBelowPct(u, role, rt);
  return (
    pickInRangeHealNeed(u, allies, pct, enemies) ??
    healSplitAlly(u, allies, pct, rt.bondHealSplitMul(u))
  );
}

/** Cast id: only staff-range hurt allies (never wait on a far split slot). */
function pickHealCastNeed(
  u: RaidUnit,
  allies: readonly RaidUnit[],
  role: RaidClassId,
  rt: KitRuntime,
  enemies: readonly RaidUnit[] = [],
  time = 0,
): RaidUnit | null {
  const pct = healBelowPct(u, role, rt);
  const base = pickInRangeHealNeed(u, allies, pct, enemies);
  return resolveSoftSafeHealNeed(u, allies, enemies, pct, base, time);
}

type PositionSpec = {
  mode:
    | "standFeet"
    | "spaceFromBoss"
    | "spaceFromThreat"
    | "chaseNearestPackHostile"
    | "bossRing"
    | "kiteFromTarget"
    | "chaseTarget"
    | "standOrChase"
    | "holdMeleeGap"
    | "healerSupport"
    | "retreatToHealer";
  standMul?: number;
  minDistMode?: "kiteRing";
  minDistCombatKey?: string;
  onlyWhenCloser?: boolean;
  slotNearBoss?: boolean;
  gapCombatKey?: string;
  gapKnob?: string;
  gapBossOnlyKnob?: string;
  stepPx?: number;
  /** Prefer over literal stepPx when set (combat table key). */
  stepPxCombatKey?: string;
  healerBias?: number;
};

type FireMode =
  | "stickyEngage"
  | "healOrSticky"
  | "meleeReach"
  | "tankMeleePad"
  /** Soft/AA space: peel in-reach thrall/charger before sticky boss. */
  | "opportunityOrSticky";

type InterruptSpec = { knob: string };

type KitDef = {
  targetPick?: string;
  role: RaidClassId;
  branches?: Array<{
    when: WhenClause;
    position: PositionSpec;
    fire?: FireMode;
    interrupt?: InterruptSpec;
  }>;
  post?: {
    /**
     * @deprecated Party encircle is owned by adjutant. Kept for gesture
     * `slotAtAnchor` assist only when a formation ref is active.
     */
    slotBoss?: boolean;
    slotBossMul?: number;
    /** When true and a gesture formation ref is active, slot on ref→boss ring. */
    slotAtAnchor?: boolean;
    /** @deprecated Adjutant owns non-gesture encircle; ignored. */
    slotBossWhen?: "noGestureAnchor";
    clampAddReach?: boolean;
    resolveBoundsOnLowHpKite?: boolean;
    skipPostInWeaponRange?: boolean;
  };
  score?: {
    knob?: string;
    requiresSpaceGoal?: boolean;
    value: number;
    /** HP% below grit `surviveHpThr`. */
    hpBelowSurviveThr?: boolean;
    requiresHealerAlive?: boolean;
    underPressureSec?: number;
    underPressureSecCombatKey?: string;
  };
  position?: PositionSpec;
  fire?: FireMode;
  interrupt?: InterruptSpec;
};

function kitById(kitId: string): KitDef {
  return kitByIdLoose(kitId) as KitDef;
}

/** Soft-pull kit goals off playBounds lips toward allies (gesture ref exempt). */
function pullKitGoalOffLip(
  u: RaidUnit,
  goal: { tx: number; ty: number },
  allies: readonly RaidUnit[],
  time: number,
  lipPx = 32,
): { tx: number; ty: number } {
  if (formationReference(u, time) != null) return goal;
  const b = raidPlayBounds();
  const edgeProx = Math.min(
    goal.tx - b.xMin - u.radius,
    b.xMax - u.radius - goal.tx,
    goal.ty - b.yMin - u.radius,
    b.yMax - u.radius - goal.ty,
  );
  if (edgeProx >= lipPx) return goal;
  const anchor = allyAnchor(u, allies);
  const pull = (lipPx - Math.max(0, edgeProx)) / lipPx;
  const t = 0.55 + 0.35 * pull;
  return resolveGoalInBounds(
    u,
    goal.tx + (anchor.x - goal.tx) * t,
    goal.ty + (anchor.y - goal.ty) * (t * 0.55),
  );
}

function resolvePosition(
  pos: PositionSpec,
  args: {
    u: RaidUnit;
    boss: RaidBoss;
    allies: readonly RaidUnit[];
    enemies: readonly RaidUnit[];
    target: EngageTarget;
    engageRing: number;
    combat: CombatTable;
    rt: KitRuntime;
    role: RaidClassId;
    time: number;
    addInWeaponRange: boolean;
    includeBoss?: boolean;
    aaClearPad?: number;
  },
): { tx: number; ty: number } {
  const { u, boss, allies, target, engageRing, combat, rt, role, time } = args;
  const aaClearPad = Math.max(0, args.aaClearPad ?? 0);
  const formOpts = { time };
  switch (pos.mode) {
    case "standFeet":
      return { tx: u.x, ty: u.y };
    case "chaseTarget":
      return { tx: target.x, ty: target.y };
    case "standOrChase":
      return args.addInWeaponRange
        ? { tx: u.x, ty: u.y }
        : { tx: target.x, ty: target.y };
    case "chaseNearestPackHostile": {
      // Tank hold: close on densest local pack, not peel to boss ring.
      let best: { x: number; y: number; radius: number } | null = null;
      let bestD = Infinity;
      const packR = raidPackRadius();
      for (const e of args.enemies) {
        if (e.team !== 1 || e.hp <= 0 || !e.combatArmed) continue;
        const d = Math.hypot(e.x - u.x, e.y - u.y);
        if (d > packR + e.radius) continue;
        if (d < bestD) {
          bestD = d;
          best = e;
        }
      }
      if (!best) {
        return args.addInWeaponRange
          ? { tx: u.x, ty: u.y }
          : { tx: target.x, ty: target.y };
      }
      const reach = addAttackReach(u, best.radius);
      if (bestD < reach) return { tx: u.x, ty: u.y };
      return { tx: best.x, ty: best.y };
    }
    case "spaceFromThreat": {
      // Plant only when clear of every armed enemy's hit reach (kiting floor)
      // plus 자기보호 margin (0 while charge corridor active).
      if (
        role !== "tank" &&
        clearOfEnemyAttack(u.x, u.y, u.radius, args.enemies, 2 + aaClearPad)
      ) {
        return { tx: u.x, ty: u.y };
      }
      // Soft≪AA FEAR: pack-escape before Role fleeDist crawl for ANY role in
      // spaceFromThreat (survive). retreatToHealer stays tank|multi to avoid
      // Soft N20 redistribution. Geometry; no Claim B.
      if (!clearOfPackChewAttack(u.x, u.y, u.radius, args.enemies, 2)) {
        const escape = resolvePackEscapeGoal(
          u,
          args.enemies,
          aaClearPad,
          allies,
        );
        if (escape) return escape;
      }
      const tank = frontTank(allies);
      // Tank must not use self as pull/cap anchor ??that collapses hold into
      // continuous self-relative kite. Prefer other-ally COM, else front tank.
      let anchor: { x: number; y: number } | null = null;
      if (role === "tank") {
        let sx = 0;
        let sy = 0;
        let n = 0;
        for (const a of allies) {
          if (a.hp <= 0 || a.id === u.id) continue;
          sx += a.x;
          sy += a.y;
          n += 1;
        }
        anchor = n > 0 ? { x: sx / n, y: sy / n } : null;
      } else if (tank) {
        anchor = { x: tank.x, y: tank.y };
      } else {
        let sx = 0;
        let sy = 0;
        let n = 0;
        for (const a of allies) {
          if (a.hp <= 0 || a.id === u.id) continue;
          sx += a.x;
          sy += a.y;
          n += 1;
        }
        anchor = n > 0 ? { x: sx / n, y: sy / n } : null;
      }
      const fleeDist = raidPackFleeDist(role) * rt.measureFleeMul(u);
      const follow = dispositionKnob(dispositionOf(u), "adjutantFollowMul");
      const allyPull =
        role === "tank"
          ? 0
          : raidPackAllyPull(role) * Math.max(0, follow);
      const g = spaceFromLocalHostileGoal(u, args.enemies, anchor, {
        role,
        fleeDist,
        allyPull,
        aaClearPad,
      });
      return resolveGoalInBounds(u, g.tx, g.ty);
    }
    case "bossRing": {
      const standMul = (pos.standMul ?? 0.85) * rt.measureStandMul(u);
      const stand = Math.min(boss.radius + u.attackRange * standMul, engageRing);
      const minRing = hostileHardMinSep(u, boss);
      const cur = Math.hypot(u.x - boss.x, u.y - boss.y);
      const holdEps = Math.max(8, u.attackRange * 0.06);
      // In-reach stand deadzone: soft-sep must not restart micro flee??lose.
      if (inRangeStandDeadzone(cur, stand, engageRing, holdEps)) {
        return { tx: u.x, ty: u.y };
      }
      return ringPointFromUnit(u, boss, stand, minRing, engageRing, formOpts);
    }
    case "kiteFromTarget": {
      const standMul = (pos.standMul ?? 0.9) * rt.measureStandMul(u);
      const primaryRadius = target.isBoss
        ? boss.radius
        : resolveAddRadius(args.enemies, target.id);
      const maxPrimaryDist = addAttackReach(u, primaryRadius) * 0.98;
      return kiteStandOnLocalHostile(u, target, args.enemies, {
        standMul,
        maxPrimaryDist,
        role,
        aaClearPad,
      });
    }
    case "spaceFromBoss": {
      let minDist: number;
      if (pos.minDistMode === "kiteRing") {
        minDist = Math.min(
          Math.max(combat.dpsKiteDist, combat.dpsFleeBossDist + 10),
          engageRing,
        );
      } else if (pos.minDistCombatKey) {
        minDist = combatNum(combat, pos.minDistCombatKey);
      } else {
        throw new Error("spaceFromBoss requires minDistMode or minDistCombatKey");
      }
      minDist *= rt.measureFleeMul(u);
      const spaced = spaceOutBossGoal(u, boss, allies, minDist, {
        onlyWhenCloser: pos.onlyWhenCloser,
        ...formOpts,
      });
      if (!spaced) {
        return { tx: u.x, ty: u.y };
      }
      return spaced;
    }
    case "holdMeleeGap": {
      let tx = target.x;
      let ty = target.y;
      const gapKnob = pos.gapKnob ?? "holdMeleeGap";
      const applyGap = knobOn(role, gapKnob);
      if (applyGap) {
        const bossOnlyKnob = pos.gapBossOnlyKnob ?? "holdMeleeGapBossOnly";
        const gapBossOnly =
          (classKnobs(role) as Record<string, unknown>)[bossOnlyKnob] !== false;
        if (target.isBoss || !gapBossOnly) {
          const gapKey = pos.gapCombatKey ?? "tankMeleeGap";
          const surfaceGap = target.isBoss
            ? combatNum(combat, gapKey)
            : Math.min(combatNum(combat, gapKey), u.attackRange * 0.85);
          // Boss: stand on surface ring (radius + unit.radius + gap), not raw
          // center distance ??tankMeleeGap used to be a center gap (< boss.radius).
          // Cap into [hardMinSep, attackReach ??eps] so tanks AA on the sep ring.
          let standDist = target.isBoss
            ? boss.radius + u.radius + surfaceGap
            : surfaceGap;
          if (target.isBoss) {
            const minSep = hostileHardMinSep(u, boss);
            const reach = boss.radius + u.attackRange;
            const epsRaw = (combat as { bodySepReachEps?: number })
              .bodySepReachEps;
            const eps = typeof epsRaw === "number" ? epsRaw : 2;
            standDist = Math.min(
              Math.max(standDist, minSep),
              Math.max(minSep, reach - eps),
            );
          }
          const dx = u.x - target.x;
          const dy = u.y - target.y;
          const len = Math.hypot(dx, dy) || 1;
          tx = target.x + (dx / len) * standDist;
          ty = target.y + (dy / len) * standDist;
        }
      }
      return { tx, ty };
    }
    case "healerSupport": {
      // Soft≪AA FEAR alone ablate: personal packChew AA outranks heal-approach.
      // Distinct from rejected survive decide overwrite (Soft N20 0.65).
      if (!clearOfPackChewAttack(u.x, u.y, u.radius, args.enemies, 2)) {
        const escape = resolvePackEscapeGoal(
          u,
          args.enemies,
          aaClearPad,
          allies,
        );
        if (escape) return escape;
      }
      const knobs = classKnobs(role) as Record<string, unknown>;
      const need = pickHealNeed(u, allies, role, rt, args.enemies);
      const tank = frontTank(allies);
      const stay = !!knobs.stayBehindTank;
      const includeBoss = args.includeBoss !== false;
      const healers = livingHealersRanked(allies);
      const healerIdx = healers.findIndex((h) => h.id === u.id);
      const backlineHint =
        tank != null
          ? healerBacklineBehindTank(
              u,
              tank,
              threatFacingForBackline(tank, boss, args.enemies, includeBoss),
              {
                healerIndex: Math.max(0, healerIdx),
                healerCount: Math.max(1, healers.length),
                enemies: args.enemies,
                boss: includeBoss ? boss : null,
              },
            )
          : null;
      if (need || (stay && tank)) {
        return pickHealerSupportGoal(
          u,
          boss,
          need,
          tank,
          time,
          stay,
          args.enemies,
          includeBoss,
          backlineHint,
        );
      }
      // No heal need: adjutant-owned tank backline (facing-gated).
      if (backlineHint) {
        return resolveGoalInBounds(u, backlineHint.x, backlineHint.y);
      }
      return { tx: u.x, ty: u.y };
    }
    case "retreatToHealer": {
      // Soft≪AA FEAR: pack-escape before healer-ward through thrall AA.
      // Tank any InAa; non-tank multi (aa≥2) only — see spaceFromThreat.
      if (!clearOfPackChewAttack(u.x, u.y, u.radius, args.enemies, 2)) {
        const escape = resolvePackEscapeGoal(
          u,
          args.enemies,
          aaClearPad,
          allies,
        );
        if (
          escape &&
          (role === "tank" || (u.brain.thrallEscapeAa ?? 0) >= 2)
        ) {
          return escape;
        }
      }
      const stepPx = pos.stepPxCombatKey
        ? combatNum(combat, pos.stepPxCombatKey)
        : (pos.stepPx ?? 70);
      const healerBias = pos.healerBias ?? 1.2;
      const g = retreatToHealerGoal(
        u,
        boss,
        allies,
        args.enemies,
        time,
        stepPx,
        healerBias,
      );
      return g ?? { tx: u.x, ty: u.y };
    }
    default: {
      const _exhaustive: never = pos.mode;
      return _exhaustive;
    }
  }
}

function applyFire(
  fire: FireMode | undefined,
  args: {
    u: RaidUnit;
    boss: RaidBoss;
    allies: readonly RaidUnit[];
    enemies: readonly RaidUnit[];
    target: EngageTarget;
    role: RaidClassId;
    focusBoss: boolean;
    time: number;
    includeBoss?: boolean;
    tx: number;
    ty: number;
    bossReach: number;
    addReach: number;
    rt: KitRuntime;
  },
): AiIntent {
  const {
    u,
    boss,
    allies,
    enemies,
    target,
    role,
    focusBoss,
    time,
    tx,
    ty,
    bossReach,
    addReach,
    rt,
  } = args;
  const mode = fire ?? "stickyEngage";

  if (mode === "healOrSticky") {
    const need = pickHealCastNeed(u, allies, role, rt, enemies, time);
    if (need) {
      return {
        tx,
        ty,
        wantAttack: false,
        wantHealId: need.id,
        wantInterrupt: false,
        ...facePoint(need.x, need.y),
        attackTargetId: null,
      };
    }
  }

  // Soft∪AA space: shoot thralls/chargers already chewing before sticky boss.
  if (mode === "opportunityOrSticky") {
    const inReach = pickInWeaponReachAdd(u, enemies);
    if (inReach) {
      return {
        tx,
        ty,
        wantAttack: true,
        wantHealId: null,
        wantInterrupt: false,
        faceX: inReach.x,
        faceY: inReach.y,
        attackTargetId: inReach.id,
      };
    }
  }

  const hostile =
    mode === "healOrSticky" ||
    mode === "stickyEngage" ||
    mode === "opportunityOrSticky"
      ? stickyEngageTarget(
          u,
          boss,
          enemies,
          role,
          focusBoss,
          time,
          args.includeBoss,
          allies,
        )
      : target;
  const d = dist(u.x, u.y, hostile.x, hostile.y);
  const reach = hostile.isBoss
    ? bossReach
    : addAttackReach(u, resolveAddRadius(enemies, hostile.id));

  let wantAttack: boolean;
  if (mode === "meleeReach") {
    wantAttack = d < (hostile.isBoss ? bossReach : addReach);
  } else if (mode === "tankMeleePad") {
    // Match Hit gate (`d <= boss.radius + attackRange` / add `< reach`).
    wantAttack = hostile.isBoss
      ? d <= u.attackRange + boss.radius
      : d < u.attackRange + 14;
  } else {
    wantAttack = hostile.isBoss ? d <= reach : d < reach;
  }

  return {
    tx,
    ty,
    wantAttack,
    wantHealId: null,
    wantInterrupt: false,
    ...faceTarget(hostile),
  };
}

function applyInterrupt(
  interrupt: InterruptSpec | undefined,
  intent: AiIntent,
  args: {
    role: RaidClassId;
    allies: readonly RaidUnit[];
    boss: RaidBoss;
    rt: KitRuntime;
  },
): AiIntent {
  if (!interrupt) return intent;
  if (!knobOn(args.role, interrupt.knob)) return intent;
  if (args.boss.telegraph == null) return intent;
  if (!allyInBrass(args.allies, args.boss)) return intent;
  return {
    ...intent,
    wantAttack: true,
    wantInterrupt: true,
    ...faceBoss(args.boss),
    attackTargetId: null,
  };
}

/** Decide movement/fire from a kit id. */
export function decideFromKit(
  kitId: string,
  ctx: KitDecideCtx,
  rt: KitRuntime,
): AiIntent {
  const kit = kitById(kitId);
  const role = kit.role;
  const combat = combatTable();
  const { u, boss, allies, enemies, focusBoss, time } = ctx;
  const includeBoss = ctx.includeBoss !== false;
  const formOpts = { time };
  const bossReach = hostileAttackReach(u, boss);
  const hardMin = hostileHardMinSep(u, boss);
  const epsRaw = (combat as { bodySepReachEps?: number }).bodySepReachEps;
  const reachEps = typeof epsRaw === "number" ? epsRaw : 2;
  const engageRing = Math.min(
    Math.max(bossReach * 0.92, hardMin),
    Math.max(hardMin, bossReach - reachEps),
  );

  // ???? single-position kits (space / support / tank critical) ????
  if (kit.position && !kit.branches) {
    const target = stickyEngageTarget(
      u,
      boss,
      enemies,
      role,
      focusBoss,
      time,
      includeBoss,
      allies,
    );
    const addReach = addAttackReach(u, resolveAddRadius(enemies, target.id));
    const pos = kit.position;
    const dangerZones = collectRaidDangerZones(boss, {
      enemies,
      includeBoss,
    });
    let goal = resolvePosition(pos, {
      u,
      boss,
      allies,
      enemies,
      target,
      engageRing,
      combat,
      rt,
      role,
      time,
      addInWeaponRange: !target.isBoss && dist(u.x, u.y, target.x, target.y) < addReach,
      includeBoss,
      aaClearPad: effectiveAaClearPadPx(u, dangerZones),
    });
    if (pos.slotNearBoss) {
      const ref = formationReference(u, time);
      if (ref) {
        goal = applySlotNearHostile(
          goal.tx,
          goal.ty,
          u,
          boss,
          engageRing,
          formOpts,
        );
      } else {
        const adj = planAdjutant({
          boss,
          allies,
          enemies,
          time,
          includeBoss,
          spawnHeat: ctx.spawnHeat,
        });
        const seed = slotFor(adj, u.id);
        if (seed) {
          const followMul = dispositionKnob(
            dispositionOf(u),
            "adjutantFollowMul",
          );
          if (followMul > 0.05) {
            const kitTx = goal.tx;
            const kitTy = goal.ty;
            const blended = blendSpaceGoalWithAdjutant(
              kitTx,
              kitTy,
              seed,
              adj.weights,
            );
            const f = followUnderScreen(
              followMul,
              adj.weights.screen,
              adj.weights.spread,
            );
            goal = {
              tx: kitTx + (blended.tx - kitTx) * f,
              ty: kitTy + (blended.ty - kitTy) * f,
            };
          }
        }
      }
    }
    if (pos.mode === "spaceFromBoss" || pos.mode === "retreatToHealer" || pos.mode === "spaceFromThreat") {
      goal = resolveGoalInBounds(u, goal.tx, goal.ty);
    }
    goal = pullKitGoalOffLip(u, goal, allies, time, 22);
    // healerSupport already bounds-resolved inside helper / ring path
    {
      const zones = collectRaidDangerZones(boss, {
        enemies,
        includeBoss,
      });
      if (zones.length > 0 && instinctAllowsAoEDodge(instinctOf(u))) {
        goal = finalizeAllyMoveGoal(u, goal.tx, goal.ty, zones, 1);
      } else if (zones.length > 0 && unitInDangerZones(u, zones)) {
        // Polar low + feet in paint: fight in place (≠ finalize escape).
        goal = { tx: u.x, ty: u.y };
      }
    }
    const intent = applyFire(kit.fire, {
      u,
      boss,
      allies,
      enemies,
      target,
      role,
      focusBoss,
      time,
      includeBoss,
      tx: goal.tx,
      ty: goal.ty,
      bossReach,
      addReach,
      rt,
    });
    return applyInterrupt(kit.interrupt, intent, { role, allies, boss, rt });
  }

  // Sticky/engage kits — adjutant seeds + weapon-band plant.
  const adjCtx = {
    boss,
    allies,
    enemies,
    time,
    includeBoss,
    spawnHeat: ctx.spawnHeat,
  };
  const adjSnap = planAdjutant(adjCtx);
  const engageOpts = {
    facing: adjSnap.facing,
    bossAnchorTankId: adjSnap.bossAnchorTankId,
  };
  const isBossAnchor =
    adjSnap.bossAnchorTankId != null && u.id === adjSnap.bossAnchorTankId;
  const target = stickyEngageTarget(
    u,
    boss,
    enemies,
    role,
    focusBoss,
    time,
    includeBoss,
    allies,
    engageOpts,
  );
  const addReach = addAttackReach(u, resolveAddRadius(enemies, target.id));
  const addInWeaponRange =
    !target.isBoss && dist(u.x, u.y, target.x, target.y) < addReach;
  const engageInReach = target.isBoss
    ? dist(u.x, u.y, boss.x, boss.y) <= bossReach
    : addInWeaponRange;
  const packSnap = localHostileSnapshot({
    x: u.x,
    y: u.y,
    unitRadius: u.radius,
    enemies,
    // Enemy Soft only — boss body must not force space over bossRing.
    includeBoss: false,
  });
  const packPressure = packSnap.packPressure;
  const inEnemyMelee = packSnap.inMelee;
  const nearEnemyHit = nearEnemyHitOrMelee(u.x, u.y, u.radius, enemies, 0);
  const packChewInAa = !clearOfPackChewAttack(
    u.x,
    u.y,
    u.radius,
    enemies,
    2,
  );
  const thrallInAa = packChewInAa;
  const softIntensity = packSnap.intensity;
  const dangerZones = collectRaidDangerZones(boss, {
    enemies,
    includeBoss,
  });

  let matched = kit.branches?.[kit.branches.length - 1];
  for (const branch of kit.branches ?? []) {
    if (
      whenMatches(branch.when, {
        addInWeaponRange,
        engageInReach,
        targetIsBoss: target.isBoss,
        packPressure,
        inEnemyMelee,
        nearEnemyHit,
        thrallInAa,
        packChewInAa,
        softIntensity,
        isBossAnchor,
        u,
        role,
        combat,
        rt,
        enemies,
        dangerZones,
      })
    ) {
      matched = branch;
      break;
    }
  }
  if (!matched) {
    throw new Error(`Kit ${kitId} matched no branch`);
  }

  let { tx, ty } = resolvePosition(matched.position, {
    u,
    boss,
    allies,
    enemies,
    target,
    engageRing,
    combat,
    rt,
    role,
    time,
    addInWeaponRange,
    includeBoss,
    aaClearPad: effectiveAaClearPadPx(u, dangerZones),
  });

  const post = kit.post ?? {};
  const surviveThr = rt.gritSurviveThr(u);
  const lowHpKite =
    knobOn(role, "kiteIfHpLow") &&
    surviveThr > 0 &&
    u.hp / u.maxHp < surviveThr;
  const bossSlotRingRaw =
    post.slotBossMul != null ? bossReach * post.slotBossMul : engageRing;
  const bossSlotRing = Math.min(
    Math.max(bossSlotRingRaw, hardMin),
    Math.max(hardMin, bossReach - reachEps),
  );
  const ref = formationReference(u, time);

  // Adjutant owns party encircle/screen seeds; kit keeps micro + gesture assist.
  // Sticky in-range plant skips adjutant unless party is clumped (wave pile).
  let slotted: { tx: number; ty: number };
  let keepStickyPlant = false;
  const followMul = dispositionKnob(dispositionOf(u), "adjutantFollowMul");
  // Near-fight gesture: ring assist so AA stay viable. Far gesture: world-hold
  // so adjutant/screen seeds cannot yank feet off the user-presented point.
  const gestureNearBossFight =
    ref != null &&
    target.isBoss &&
    !!post.slotAtAnchor &&
    dist(ref.x, ref.y, boss.x, boss.y) <= bossReach * 1.08;

  if (matched.position.mode === "retreatToHealer") {
    slotted = { tx, ty };
  } else if (gestureNearBossFight) {
    // Gesture assist: slot on formation-ref ring (applySlotNearHostile prefers ref).
    slotted = applySlotNearHostile(tx, ty, u, boss, bossSlotRing, formOpts);
  } else if (ref != null) {
    const w = gestureFormationWeight();
    slotted = {
      tx: tx + (ref.x - tx) * w,
      ty: ty + (ref.y - ty) * w,
    };
  } else if (
    shouldSoftLateralAdjutantSeed({
      hasGestureRef: false,
      positionMode: matched.position.mode,
    })
  ) {
    slotted = { tx, ty };
    if (
      matched.position.mode === "spaceFromThreat" &&
      role !== "tank" &&
      !target.isBoss &&
      post.clampAddReach &&
      dist(tx, ty, target.x, target.y) > addReach * 0.96
    ) {
      slotted = clampGoalToAddReach(tx, ty, u, target, addReach);
    }
    const adj = adjSnap;
    const seed = slotFor(adj, u.id);
    // Tanks keep hold/chase micro; soft-lateral is for kite/skirmish backline.
    const packAsk =
      role !== "tank" &&
      (adj.signals.clumpPenalty >= 0.35 ||
        adj.weights.spread >= 0.55 ||
        adj.weights.screen >= 0.55);
    if (seed && packAsk && followMul > 0.05) {
      const screen = adj.weights.screen;
      slotted = worldLerpTowardAdjutantSeed(
        slotted.tx,
        slotted.ty,
        seed,
        adj.weights,
        0.35 + 0.3 * screen,
        followUnderScreen(followMul, screen, adj.weights.spread),
      );
    }
  } else if (matched.position.mode === "healerSupport") {
    const adj = adjSnap;
    const rawSeed = slotFor(adj, u.id);
    const j = judgmentOf(u);
    const pct = healBelowPct(u, role, rt);
    const subject = pickCoverSubject(allies, pct, u.id);
    const healers = livingHealers(allies);
    const subjectOut =
      subject != null && coverSubjectOutOfStaff(subject, healers);
    if (rawSeed && followMul > 0.05) {
      const tank = frontTank(allies);
      const healerInPackChewAa = !clearOfPackChewAttack(
        u.x,
        u.y,
        u.radius,
        enemies,
        2,
      );
      const rescued = biasAdjutantSeedForRescue({
        healer: u,
        subject,
        bond: j.bond,
        seed: rawSeed,
        subjectOutOfStaff: subjectOut,
        time,
        healerInPackChewAa,
        tank,
      });
      const seed = { x: rescued.x, y: rescued.y };
      // Soft≪AA bond local accept (≠ rescue pull matrix / crisis peel):
      // healer clear + ally chewed but still in staff → accept Soft-clear
      // adjutant seed more (propose/accept, no OOR chase).
      const allyChewedInStaff =
        !healerInPackChewAa &&
        subject != null &&
        !subjectOut &&
        allies.some(
          (a) =>
            a.id === subject.unitId &&
            a.hp > 0 &&
            !clearOfPackChewAttack(a.x, a.y, a.radius, enemies, 2),
        ) &&
        clearOfPackChewAttack(
          rawSeed.x,
          rawSeed.y,
          u.radius,
          enemies,
          2,
        );
      // Soft plateau: always use legacy formation follow weight.
      // Rescue only soft-moves seed XY — never mute follow during crisis.
      const need = pickHealNeed(u, allies, role, rt, enemies);
      const screen = adj.weights.screen;
      const formW = Math.max(
        adj.weights.encircle,
        screen,
        0.35 + 0.25 * screen,
      );
      const base = need ? (allyChewedInStaff ? 0.58 : 0.4) : 0.75;
      const legacyFollow = followUnderScreen(
        followMul,
        screen,
        adj.weights.spread,
      );
      const k =
        legacyFollow *
        base *
        (0.5 + 0.5 * formW);
      if (k > 0.02) {
        slotted = {
          tx: tx + (seed.x - tx) * k,
          ty: ty + (seed.y - ty) * k,
        };
      } else {
        slotted = { tx, ty };
      }
    } else {
      slotted = { tx, ty };
    }
  } else if (
    shouldConsumeAdjutantSeed({
      hasGestureRef: false,
      positionMode: matched.position.mode,
      targetIsBoss: target.isBoss,
    })
  ) {
    const adj = adjSnap;
    const seed = slotFor(adj, u.id);
    if (seed && followMul > 0.05) {
      const mode = matched.position.mode;
      const fScreen = followUnderScreen(
        followMul,
        adj.weights.screen,
        adj.weights.spread,
      );
      if (mode === "bossRing" && target.isBoss) {
        slotted = blendKitRingWithAdjutantSeed(
          tx,
          ty,
          seed,
          { x: boss.x, y: boss.y },
          0.62 * fScreen,
        );
      } else if (mode === "kiteFromTarget" && target.isBoss) {
        slotted = blendKitRingWithAdjutantSeed(
          tx,
          ty,
          seed,
          { x: boss.x, y: boss.y },
          0.62 * fScreen,
        );
      } else if (mode === "holdMeleeGap" && target.isBoss) {
        slotted = {
          tx: tx + (seed.x - tx) * fScreen,
          ty: ty + (seed.y - ty) * fScreen,
        };
      } else if (
        mode === "kiteFromTarget" ||
        mode === "chaseTarget" ||
        mode === "holdMeleeGap"
      ) {
        // Sticky AA plant when party not clumped; wave pile still takes world seed.
        // Use clumpPenalty (not aoeHazard) so boss-danger sticky AA stays planted.
        const clumpAsk =
          adj.signals.clumpPenalty >= 0.35 || adj.weights.screen >= 0.7;
        if (addInWeaponRange && post.skipPostInWeaponRange && !clumpAsk) {
          keepStickyPlant = true;
          slotted = { tx, ty };
        } else {
          const baseK = addInWeaponRange ? 0.35 : 0.65;
          slotted = worldLerpTowardAdjutantSeed(
            tx,
            ty,
            seed,
            adj.weights,
            baseK,
            fScreen,
          );
        }
      } else {
        slotted = {
          tx: tx + (seed.x - tx) * fScreen,
          ty: ty + (seed.y - ty) * fScreen,
        };
      }
      if (
        !target.isBoss &&
        post.clampAddReach &&
        dist(slotted.tx, slotted.ty, target.x, target.y) > addReach * 0.96
      ) {
        slotted = clampGoalToAddReach(
          slotted.tx,
          slotted.ty,
          u,
          target,
          addReach,
        );
      }
    } else if (post.clampAddReach && !target.isBoss) {
      const s = applySlot(tx, ty, u);
      slotted = clampGoalToAddReach(s.tx, s.ty, u, target, addReach);
    } else {
      slotted = { tx, ty };
    }
  } else if (post.clampAddReach && !target.isBoss) {
    const s = applySlot(tx, ty, u);
    slotted = clampGoalToAddReach(s.tx, s.ty, u, target, addReach);
  } else {
    slotted = { tx, ty };
  }

  if (!addInWeaponRange && post.resolveBoundsOnLowHpKite && lowHpKite) {
    slotted = resolveGoalInBounds(u, slotted.tx, slotted.ty);
  }

  // Measure plant after formation slot — only when SoftThreat is plant-safe
  // and feet are outside enemy hit reach (+ 자기보호 margin).
  if (
    softIntensity <= raidSoftPlantMax() &&
    clearOfEnemyAttack(
      u.x,
      u.y,
      u.radius,
      enemies,
      2 + effectiveAaClearPadPx(u, dangerZones),
    ) &&
    (matched.position.mode === "bossRing" ||
      matched.position.mode === "kiteFromTarget")
  ) {
    const inReach = target.isBoss
      ? dist(u.x, u.y, boss.x, boss.y) <= bossReach
      : addInWeaponRange;
    slotted = applyMeasurePlantGoal(
      u,
      slotted,
      insightAxisOf(dispositionOf(u)),
      inReach,
    );
  }

  // Lip pull for non-hold tank pack modes only — tanks must keep pack feet.
  // Sticky weapon-reach plant must not be yanked toward ally (breaks reach assert).
  // spaceFromThreat never lip-pulls (would cancel SoftThreat clearance).
  const skipLip =
    keepStickyPlant ||
    matched.position.mode === "spaceFromThreat" ||
    (role === "tank" &&
      (matched.position.mode === "chaseNearestPackHostile" ||
        matched.position.mode === "standOrChase" ||
        matched.position.mode === "holdMeleeGap"));
  if (!skipLip) {
    slotted = pullKitGoalOffLip(u, slotted, allies, time, 22);
  }

  // SoftThreat omits DangerZones from its field — still never plant kit goals
  // on active leap/charge/telegraph floors, and never walk a segment through them.
  // Polar low: skip finalize escape (fight-in-place when already painted).
  {
    const zones = collectRaidDangerZones(boss, {
      enemies,
      includeBoss,
    });
    if (zones.length > 0 && instinctAllowsAoEDodge(instinctOf(u))) {
      slotted = finalizeAllyMoveGoal(u, slotted.tx, slotted.ty, zones, 1);
    } else if (zones.length > 0 && unitInDangerZones(u, zones)) {
      slotted = { tx: u.x, ty: u.y };
    }
  }

  const fire = matched.fire ?? kit.fire;
  const intent = applyFire(fire, {
    u,
    boss,
    allies,
    enemies,
    target,
    role,
    focusBoss,
    time,
    includeBoss,
    tx: slotted.tx,
    ty: slotted.ty,
    bossReach,
    addReach,
    rt,
  });
  return applyInterrupt(matched.interrupt ?? kit.interrupt, intent, {
    role,
    allies,
    boss,
    rt,
  });
}

export type ClassLoadout = {
  chips?: Record<string, string>;
  fallback?: string;
  fleeBossDistKey?: string;
  ruleStack?: {
    dodge?: "tank" | "ranged";
    tier2?: string;
    orders?: boolean;
  };
};

/** Chip id ??kit/builtin id for a class loadout. */
export function kitIdForChip(
  classId: RaidClassId,
  chipId: string,
): string | null {
  const loadouts = raidCatalog.behaviorKits.classLoadouts as Record<
    string,
    ClassLoadout
  >;
  return loadouts[classId]?.chips?.[chipId] ?? null;
}

export function classLoadout(classId: RaidClassId): ClassLoadout {
  const loadouts = raidCatalog.behaviorKits.classLoadouts as Record<
    string,
    ClassLoadout
  >;
  const lo = loadouts[classId];
  if (!lo) throw new Error(`Missing classLoadout for ${classId}`);
  return lo;
}

/** True when ref is a kit id in ai-behavior-kits.json. */
export function isBehaviorKitId(ref: string): boolean {
  const kits = raidCatalog.behaviorKits.kits as Record<string, unknown>;
  return Object.prototype.hasOwnProperty.call(kits, ref);
}
